import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { projectId } from '../utils/supabase/info';
import { 
  ArrowLeft, 
  Settings, 
  List, 
  Plus, 
  Lock, 
  CheckCircle2, 
  AlertCircle,
  Clock,
  Shield,
  GitBranch,
  FileText,
  Save,
  Pencil,
  X,
  Info,
  LayoutTemplate,
  AlertTriangle,
  Building2,
  Calendar,
  PauseCircle,
  Share2,
  Users,
  Trash2,
  Flame,
  Zap,
  Server,
  GitPullRequest
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { RequestTypeFormBuilder } from './RequestTypeFormBuilder';
import { SpaceArchitectureWizard, SpaceArchitecture } from './SpaceArchitectureWizard';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { GuidanceButton } from './GuidanceButton';
import { toast } from 'sonner';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';

// Workflow Options Definition
const WORKFLOW_OPTIONS = [
  { 
    id: 'service-request-fulfillment', 
    label: 'Service Request Fulfillment', 
    description: 'Standard Atlassian workflow for handling service requests with approval steps.' 
  },
  { 
    id: 'incident-management', 
    label: 'Incident Management', 
    description: 'Atlassian default workflow for tracking and resolving incidents.' 
  },
  { 
    id: 'change-management', 
    label: 'Change Management', 
    description: 'Comprehensive workflow for Standard, Normal, and Emergency changes.' 
  },
  { 
    id: 'problem-management', 
    label: 'Problem Management', 
    description: 'Workflow for root cause analysis and problem resolution.' 
  },
  { 
    id: 'post-incident-review', 
    label: 'Post-Incident Review', 
    description: 'Process for analyzing major incidents after resolution.' 
  }
];

// Types
interface SLACalendar {
  type: '24x7' | 'business_hours';
  timezone: string;
  businessHours: {
    days: string[];
    start: string;
    end: string;
  };
}

interface SLAPauseConditions {
  pauseStatuses: string[];
  pauseOutsideBusinessHours: boolean;
}

interface ChannelConfig {
  portal: boolean;
  email: boolean;
  api: boolean;
  chat: boolean;
  emailSettings?: {
    createRequests: boolean;
    defaultRequestTypeId?: string;
  };
}

interface QueueConfig {
  queues: Array<{
    id: string;
    name: string;
    description?: string;
    routingModel: 'manual' | 'direct' | 'round_robin';
    escalationTeamId?: string;
  }>;
}

interface ProblemConfig {
    enableProblemManagement: boolean;
    rcaRequirement: 'always' | 'p1_p2_only';
}

interface WorkType {
  id: string;
  name: string;
  companyId: string;
  description?: string;
  status: 'draft' | 'in_review' | 'approved';
  globalConfig: {
    workflowConfig: {
      workflowTemplateId: string;
      changeTypes?: string[];
      majorIncidentEnabled?: boolean;
    };
    sla: {
      response: Record<string, number>;
      resolution: Record<string, number>;
    };
    priorityMatrix: {
      impact: string[];
      urgency: string[];
      matrix: Record<string, string>;
    };
    // Phase 6.5 Additions
    slaCalendar: SLACalendar;
    slaPause: SLAPauseConditions;
    channels: ChannelConfig;
    queues: QueueConfig;
    
    // Change Management Specifics
    riskModel?: 'manual' | 'calculator';
    approvals?: {
        normal: string;
        emergency: string;
    };
    blackouts?: boolean;

    // Problem Management Specifics
    problemConfig?: ProblemConfig;

    // Advanced Configuration
    adv?: {
        // Incident Advanced
        model?: string;
        regions?: string[];
        onCall?: string;
        roles?: string[];
        monitoring?: string[];
        alertMap?: any;
        pirTrigger?: string;
        comms?: string[];
        
        // Change Advanced
        cab?: string[]; // Entity selector results
        cicd?: string;
        stdCatalog?: boolean;

        // Problem Advanced
        knownErrorDb?: boolean;
    }
  };
}

interface RequestType {
  id: string;
  name: string;
  description?: string;
  status: 'draft' | 'ready_for_build' | 'approved';
  workTypeId: string;
}

interface EngagementConfig {
  sowId: string;
  companyId: string;
  spaceArchitecture: SpaceArchitecture | null;
  workTypes: Record<string, {
      workTypeId: string;
      slaConfig: any;
      priorityMatrixConfig: any;
      workflowConfig: any;
      status: string;
      locked: boolean;
      // Phase 6.5
      slaCalendar?: SLACalendar;
      slaPauseConditions?: SLAPauseConditions;
      channelConfig?: ChannelConfig;
      queueConfig?: QueueConfig;
      // Change
      riskModel?: string;
      approvals?: any;
      blackouts?: boolean;
      // Problem
      problemConfig?: ProblemConfig;
      // Advanced
      adv?: any;
  }>;
}

// Defaults
const defaultSla = {
    response: { 'Critical': 1, 'High': 4, 'Medium': 8, 'Low': 24 },
    resolution: { 'Critical': 4, 'High': 24, 'Medium': 72, 'Low': 120 }
};

const defaultMatrix = {
    impact: ['High', 'Medium', 'Low'],
    urgency: ['High', 'Medium', 'Low'],
    matrix: {
        'High-High': 'Critical',
        'High-Medium': 'High',
        'Medium-High': 'High',
        'Medium-Medium': 'Medium',
        'Low-Low': 'Low',
        'High-Low': 'Medium',
        'Medium-Low': 'Low',
        'Low-High': 'Medium',
        'Low-Medium': 'Low'
    }
};

const defaultIncidentMatrix = {
    impact: ['Critical', 'High', 'Medium', 'Low'],
    urgency: ['Critical', 'High', 'Medium', 'Low'],
    matrix: {
        'Critical-Critical': 'Critical',
        'Critical-High': 'Critical',
        'Critical-Medium': 'High',
        'Critical-Low': 'Medium',
        'High-Critical': 'Critical',
        'High-High': 'High',
        'High-Medium': 'High',
        'High-Low': 'Medium',
        'Medium-Critical': 'High',
        'Medium-High': 'High',
        'Medium-Medium': 'Medium',
        'Medium-Low': 'Low',
        'Low-Critical': 'Medium',
        'Low-High': 'Medium',
        'Low-Medium': 'Low',
        'Low-Low': 'Low'
    }
};

const defaultWorkflowConfig = {
    workflowTemplateId: 'service-request-fulfillment',
    changeTypes: []
};

// Phase 6.5 Defaults
const defaultSlaCalendar: SLACalendar = {
    type: 'business_hours',
    timezone: 'UTC',
    businessHours: { days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], start: '09:00', end: '17:00' }
};

const defaultSlaPause: SLAPauseConditions = {
    pauseStatuses: ['Waiting for Customer', 'On Hold', 'Pending Approval'],
    pauseOutsideBusinessHours: true
};

const defaultChannels: ChannelConfig = {
    portal: true,
    email: false,
    api: false,
    chat: false,
    emailSettings: { createRequests: false }
};

const defaultQueues: QueueConfig = {
    queues: [
        { id: 'q1', name: 'General Queue', description: 'Default queue for triage', routingModel: 'manual' }
    ]
};

const defaultProblemConfig: ProblemConfig = {
    enableProblemManagement: true,
    rcaRequirement: 'p1_p2_only'
};

const TIMEZONES = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Australia/Sydney'
];

const PAUSE_STATUS_OPTIONS = [
    'Waiting for Customer', 'On Hold', 'Pending Approval', 'Blocked', 'Escalated', 'Waiting for Vendor'
];

export function WorkTypeDetail({ accessToken, currentUser }: { accessToken: string; currentUser: any }) {
  const { sowId, workTypeId } = useParams<{ sowId: string; workTypeId: string }>();
  const navigate = useNavigate();
  
  const [workType, setWorkType] = useState<WorkType | null>(null);
  const [requestTypes, setRequestTypes] = useState<RequestType[]>([]);
  const [engagementConfig, setEngagementConfig] = useState<EngagementConfig | null>(null);
  
  const [spaceArchitecture, setSpaceArchitecture] = useState<SpaceArchitecture | null>(null);
  const [isSpaceWizardOpen, setIsSpaceWizardOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedRequestTypeId, setSelectedRequestTypeId] = useState<string | null>(null);
  
  const [isEditingGlobal, setIsEditingGlobal] = useState(false);
  const [tempGlobalConfig, setTempGlobalConfig] = useState<WorkType['globalConfig'] | null>(null);

  // Load Data
  useEffect(() => {
    if (!workTypeId || !sowId) return;
    
    const loadData = async () => {
        setLoading(true);
        try {
            // Mock metadata for Work Type (Name/Desc)
            // In real app, fetch from generic Work Type endpoint
            const companyId = 'mock-company-123'; 
            const isIncident = workTypeId.toLowerCase().includes('incident');
            const isChange = workTypeId.toLowerCase().includes('change');
            const isProblem = workTypeId.toLowerCase().includes('problem');
            const isPIR = workTypeId.toLowerCase().includes('post-incident');
            
            let name = 'Service Request Management';
            let description = 'Handle generic service requests from the portal';
            if (isIncident) {
                name = 'Incident Management';
                description = 'Manage and resolve incidents to restore service.';
            } else if (isChange) {
                name = 'Change Management';
                description = 'Control the lifecycle of all changes to enable beneficial changes with minimum disruption.';
            } else if (isProblem) {
                name = 'Problem Management';
                description = 'Identify and resolve the root causes of incidents.';
            } else if (isPIR) {
                name = 'Post-Incident Review';
                description = 'Analyze major incidents to prevent recurrence.';
            }

            const mockMeta = {
                id: workTypeId,
                name, 
                companyId: companyId,
                description,
                status: 'draft' as const
            };

            // Fetch Canonical Engagement Config
            const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-config/${companyId}/${sowId}`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            
            if (res.ok) {
                const { engagementConfig } = await res.json();
                setEngagementConfig(engagementConfig);
                setSpaceArchitecture(engagementConfig.spaceArchitecture);

                const wtConfig = engagementConfig.workTypes?.[workTypeId];
                
                // Determine matrix default based on type if not present
                const initialMatrix = wtConfig?.priorityMatrixConfig || (isIncident ? defaultIncidentMatrix : defaultMatrix);
                
                let defaultWorkflow = defaultWorkflowConfig.workflowTemplateId;
                if (isIncident) defaultWorkflow = 'incident-management';
                if (isChange) defaultWorkflow = 'change-management';
                if (isProblem) defaultWorkflow = 'problem-management';
                if (isPIR) defaultWorkflow = 'post-incident-review';

                setWorkType({
                    ...mockMeta,
                    status: (wtConfig?.status || 'draft') as any,
                    globalConfig: {
                        workflowConfig: wtConfig?.workflowConfig || { 
                            workflowTemplateId: defaultWorkflow,
                            changeTypes: [],
                            majorIncidentEnabled: false
                        },
                        sla: wtConfig?.slaConfig || defaultSla,
                        priorityMatrix: initialMatrix,
                        slaCalendar: wtConfig?.slaCalendar || defaultSlaCalendar,
                        slaPause: wtConfig?.slaPauseConditions || defaultSlaPause,
                        channels: wtConfig?.channelConfig || defaultChannels,
                        queues: wtConfig?.queueConfig || defaultQueues,
                        riskModel: wtConfig?.riskModel,
                        approvals: wtConfig?.approvals,
                        blackouts: wtConfig?.blackouts,
                        problemConfig: wtConfig?.problemConfig || (isProblem ? defaultProblemConfig : undefined),
                        adv: wtConfig?.adv || {}
                    }
                });
            } else {
                // Fallback / First Time
                let defaultWorkflow = defaultWorkflowConfig.workflowTemplateId;
                if (isIncident) defaultWorkflow = 'incident-management';
                if (isChange) defaultWorkflow = 'change-management';
                if (isProblem) defaultWorkflow = 'problem-management';
                if (isPIR) defaultWorkflow = 'post-incident-review';

                setWorkType({
                    ...mockMeta,
                    globalConfig: {
                        workflowConfig: { 
                            workflowTemplateId: defaultWorkflow,
                            changeTypes: [],
                            majorIncidentEnabled: false
                        },
                        sla: defaultSla,
                        priorityMatrix: isIncident ? defaultIncidentMatrix : defaultMatrix,
                        slaCalendar: defaultSlaCalendar,
                        slaPause: defaultSlaPause,
                        channels: defaultChannels,
                        queues: defaultQueues,
                        problemConfig: isProblem ? defaultProblemConfig : undefined,
                        adv: {}
                    }
                });
                setEngagementConfig({
                    sowId,
                    companyId,
                    spaceArchitecture: null,
                    workTypes: {}
                });
            }

            // Mock Request Types (Layer 2)
            setRequestTypes([
                // This would normally come from DB
            ]);

        } catch (e) {
            console.error(e);
            toast.error("Failed to load configuration");
        } finally {
            setLoading(false);
        }
    };

    loadData();
  }, [sowId, workTypeId, accessToken]);

  const saveEngagementConfig = async (newConfig: EngagementConfig) => {
      try {
          const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-config/${newConfig.companyId}/${newConfig.sowId}`, {
              method: 'PUT',
              headers: { 
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify(newConfig)
          });
          
          if (res.ok) {
              setEngagementConfig(newConfig);
              return true;
          }
          return false;
      } catch (e) {
          console.error(e);
          return false;
      }
  };

  const handleOpenWizard = (requestTypeId?: string) => {
    setSelectedRequestTypeId(requestTypeId || null);
    setIsWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setIsWizardOpen(false);
    setSelectedRequestTypeId(null);
  };

  const handleStartEditing = () => {
    if (!workType) return;
    setTempGlobalConfig(JSON.parse(JSON.stringify(workType.globalConfig)));
    setIsEditingGlobal(true);
  };

  const handleCancelEditing = () => {
    setIsEditingGlobal(false);
    setTempGlobalConfig(null);
  };

  const handleSaveGlobalConfig = async () => {
    if (!workType || !tempGlobalConfig || !engagementConfig) return;
    
    // Update local state
    const updatedWorkType = {
        ...workType,
        globalConfig: tempGlobalConfig
    };
    setWorkType(updatedWorkType);
    
    // Update and persist canonical config
    const updatedEngagementConfig = {
        ...engagementConfig,
        workTypes: {
            ...engagementConfig.workTypes,
            [workTypeId!]: {
                workTypeId: workTypeId!,
                status: workType.status,
                locked: false, // Or keep existing lock state
                workflowConfig: tempGlobalConfig.workflowConfig,
                slaConfig: tempGlobalConfig.sla,
                priorityMatrixConfig: tempGlobalConfig.priorityMatrix,
                slaCalendar: tempGlobalConfig.slaCalendar,
                slaPauseConditions: tempGlobalConfig.slaPause,
                channelConfig: tempGlobalConfig.channels,
                queueConfig: tempGlobalConfig.queues,
                riskModel: tempGlobalConfig.riskModel,
                approvals: tempGlobalConfig.approvals,
                blackouts: tempGlobalConfig.blackouts,
                problemConfig: tempGlobalConfig.problemConfig,
                adv: tempGlobalConfig.adv
            }
        }
    };
    
    const success = await saveEngagementConfig(updatedEngagementConfig);
    if (success) {
        setIsEditingGlobal(false);
        toast.success("Global configuration saved");
    } else {
        toast.error("Failed to save configuration");
    }
  };

  const handleSpaceArchitectureSave = async (data: SpaceArchitecture) => {
      if (!engagementConfig) return;
      
      const updatedConfig = {
          ...engagementConfig,
          spaceArchitecture: data
      };
      
      const success = await saveEngagementConfig(updatedConfig);
      if (success) {
          setSpaceArchitecture(data);
          if (data.locked) setIsSpaceWizardOpen(false);
      } else {
          toast.error("Failed to save space architecture");
      }
  };

  // Helper to update temp config
  const updateMatrixCell = (impact: string, urgency: string, priority: string) => {
    if (!tempGlobalConfig) return;
    setTempGlobalConfig({
      ...tempGlobalConfig,
      priorityMatrix: {
        ...tempGlobalConfig.priorityMatrix,
        matrix: {
          ...tempGlobalConfig.priorityMatrix.matrix,
          [`${impact}-${urgency}`]: priority
        }
      }
    });
  };

  const updateSLA = (type: 'response' | 'resolution', priority: string, value: string) => {
    if (!tempGlobalConfig) return;
    const numValue = parseInt(value) || 0;
    setTempGlobalConfig({
      ...tempGlobalConfig,
      sla: {
        ...tempGlobalConfig.sla,
        [type]: {
          ...tempGlobalConfig.sla[type],
          [priority]: numValue
        }
      }
    });
  };

  const updateWorkflowConfig = (field: keyof WorkType['globalConfig']['workflowConfig'], value: any) => {
      if (!tempGlobalConfig) return;
      setTempGlobalConfig({
          ...tempGlobalConfig,
          workflowConfig: {
              ...tempGlobalConfig.workflowConfig,
              [field]: value
          }
      });
  };

  const toggleChangeType = (type: string) => {
      if (!tempGlobalConfig) return;
      const currentTypes = tempGlobalConfig.workflowConfig.changeTypes || [];
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter(t => t !== type)
        : [...currentTypes, type];
      
      updateWorkflowConfig('changeTypes', newTypes);
  };

  const toggleMajorIncident = (enabled: boolean) => {
      updateWorkflowConfig('majorIncidentEnabled', enabled);
  };

  // Phase 6.5 Helpers
  const updateSlaCalendar = (field: string, value: any) => {
      if (!tempGlobalConfig) return;
      setTempGlobalConfig({
          ...tempGlobalConfig,
          slaCalendar: {
              ...(tempGlobalConfig.slaCalendar || defaultSlaCalendar),
              [field]: value
          }
      });
  };

  const updateSlaPause = (field: string, value: any) => {
      if (!tempGlobalConfig) return;
      setTempGlobalConfig({
          ...tempGlobalConfig,
          slaPause: {
              ...(tempGlobalConfig.slaPause || defaultSlaPause),
              [field]: value
          }
      });
  };

  const togglePauseStatus = (status: string) => {
      if (!tempGlobalConfig) return;
      const current = tempGlobalConfig.slaPause?.pauseStatuses || [];
      const updated = current.includes(status) 
          ? current.filter(s => s !== status) 
          : [...current, status];
      updateSlaPause('pauseStatuses', updated);
  };

  const updateChannels = (field: string, value: any) => {
      if (!tempGlobalConfig) return;
      setTempGlobalConfig({
          ...tempGlobalConfig,
          channels: {
              ...(tempGlobalConfig.channels || defaultChannels),
              [field]: value
          }
      });
  };

  const updateAdvanced = (field: string, value: any) => {
      if (!tempGlobalConfig) return;
      setTempGlobalConfig({
          ...tempGlobalConfig,
          adv: {
              ...(tempGlobalConfig.adv || {}),
              [field]: value
          }
      });
  };

  const updateApproval = (type: 'normal' | 'emergency', value: string) => {
      if (!tempGlobalConfig) return;
      setTempGlobalConfig({
          ...tempGlobalConfig,
          approvals: {
              ...(tempGlobalConfig.approvals || { normal: '', emergency: '' }),
              [type]: value
          }
      });
  };

  // Problem Helpers
  const updateProblemConfig = (field: keyof ProblemConfig, value: any) => {
      if (!tempGlobalConfig) return;
      setTempGlobalConfig({
          ...tempGlobalConfig,
          problemConfig: {
              ...(tempGlobalConfig.problemConfig || defaultProblemConfig),
              [field]: value
          }
      });
  };

  const addQueue = () => {
      if (!tempGlobalConfig) return;
      const queues = tempGlobalConfig.queues?.queues || [];
      const newQueue = { 
          id: `q-${Date.now()}`, 
          name: 'New Queue', 
          description: '', 
          routingModel: 'manual' as const 
      };
      setTempGlobalConfig({
          ...tempGlobalConfig,
          queues: { queues: [...queues, newQueue] }
      });
  };

  const updateQueue = (index: number, field: string, value: any) => {
      if (!tempGlobalConfig) return;
      const queues = [...(tempGlobalConfig.queues?.queues || [])];
      queues[index] = { ...queues[index], [field]: value };
      setTempGlobalConfig({
          ...tempGlobalConfig,
          queues: { queues }
      });
  };

  const removeQueue = (index: number) => {
      if (!tempGlobalConfig) return;
      const queues = [...(tempGlobalConfig.queues?.queues || [])];
      queues.splice(index, 1);
      setTempGlobalConfig({
          ...tempGlobalConfig,
          queues: { queues }
      });
  };

  if (loading || !workType) {
    return <div className="p-8 flex justify-center"><div className="animate-spin">Loading...</div></div>;
  }

  // Derived config for display (either saved or temp if editing)
  const displayConfig = isEditingGlobal && tempGlobalConfig ? tempGlobalConfig : workType.globalConfig;
  const selectedWorkflow = WORKFLOW_OPTIONS.find(w => w.id === displayConfig.workflowConfig.workflowTemplateId);
  const isArchitectureLocked = spaceArchitecture?.locked && spaceArchitecture?.status === 'ready_for_build';
  const isChange = workType.id.toLowerCase().includes('change');
  const isIncident = workType.id.toLowerCase().includes('incident');
  const isProblem = workType.id.toLowerCase().includes('problem');
  const isPIR = workType.id.toLowerCase().includes('post-incident');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Grid
          </Button>
          <div className="h-6 w-px bg-gray-200" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">{workType.name}</h1>
            <p className="text-xs text-muted-foreground">Work Type Configuration • {workType.status.toUpperCase()}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
           {!isArchitectureLocked && (
               <Badge variant="destructive" className="animate-pulse">
                   <AlertTriangle className="h-3 w-3 mr-1" />
                   Space Arch. Missing
               </Badge>
           )}
           <Badge variant={workType.status === 'approved' ? 'default' : 'outline'}>
             {workType.status === 'approved' ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
             {workType.status.replace('_', ' ')}
           </Badge>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto p-6">
        
        {/* Architecture Warning Banner */}
        {!isArchitectureLocked && (
            <Card className="mb-6 border-l-4 border-l-amber-500 bg-amber-50">
                <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                        <Building2 className="h-6 w-6 text-amber-600" />
                        <div>
                            <h3 className="font-semibold text-amber-900">Space Architecture Decision Required</h3>
                            <p className="text-sm text-amber-700">
                                You must define and lock the Space Architecture (ITSM vs Software, Shared vs Dedicated) before this Work Type can be approved.
                            </p>
                        </div>
                    </div>
                    <Button onClick={() => setIsSpaceWizardOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white">
                        Complete Space Architecture
                    </Button>
                </CardContent>
            </Card>
        )}

        <Tabs defaultValue="global-config" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="global-config">
              <Settings className="h-4 w-4 mr-2" />
              Global Config
            </TabsTrigger>
            <TabsTrigger value="advanced-config">
              <Zap className="h-4 w-4 mr-2" />
              Advanced
            </TabsTrigger>
            <TabsTrigger value="request-types">
              <List className="h-4 w-4 mr-2" />
              Request Types
            </TabsTrigger>
          </TabsList>

          {/* TAB A: Global Configuration */}
          <TabsContent value="global-config" className="space-y-6">
            
            {/* Space Architecture Summary (Read Only) */}
            {spaceArchitecture && (
                <Card className="bg-slate-50 border-slate-200">
                    <CardHeader className="pb-2">
                        <div className="flex justify-between items-center">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <LayoutTemplate className="h-4 w-4 text-slate-500" />
                                Space Architecture
                            </CardTitle>
                            <Button variant="ghost" size="sm" onClick={() => setIsSpaceWizardOpen(true)}>
                                {spaceArchitecture.locked ? 'View Details' : 'Edit Architecture'}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                                <span className="text-xs text-slate-500 uppercase font-semibold">Space Type</span>
                                <div className="font-medium mt-1">
                                    {spaceArchitecture.spaceType === 'jsm' ? 'Jira Service Management' : spaceArchitecture.spaceType === 'jira' ? 'Jira Software' : 'Hybrid'}
                                </div>
                            </div>
                            <div>
                                <span className="text-xs text-slate-500 uppercase font-semibold">Config Strategy</span>
                                <div className="font-medium mt-1 capitalize">
                                    {formatCategoryLabel(spaceArchitecture.configStrategy)}
                                </div>
                            </div>
                            <div>
                                <span className="text-xs text-slate-500 uppercase font-semibold">Structure</span>
                                <div className="font-medium mt-1 capitalize">
                                    {spaceArchitecture.structureStrategy}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* PROBLEM MANAGEMENT CONFIGURATION (Canonical - Phase 1) */}
            {isProblem && (
                <Card className="border-indigo-200 shadow-sm">
                    <CardHeader className="bg-indigo-50/50 pb-4 border-b border-indigo-100">
                        <div className="flex justify-between items-start">
                            <div>
                                <CardTitle className="text-indigo-900 flex items-center gap-2">
                                    <Flame className="h-5 w-5 text-indigo-600" />
                                    Problem Management Policy
                                </CardTitle>
                                <CardDescription className="text-indigo-700 mt-1">
                                    Define global rules for root cause analysis (RCA) and problem management lifecycle.
                                </CardDescription>
                            </div>
                            {!isEditingGlobal ? (
                                <Button onClick={handleStartEditing} variant="outline" size="sm" className="bg-white hover:bg-indigo-50 text-indigo-700 border-indigo-200">
                                    <Pencil className="h-3 w-3 mr-2" /> Edit Policy
                                </Button>
                            ) : (
                                <div className="flex gap-2">
                                    <Button onClick={handleCancelEditing} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700">
                                        Cancel
                                    </Button>
                                    <Button onClick={handleSaveGlobalConfig} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                        <Save className="h-3 w-3 mr-2" /> Save Changes
                                    </Button>
                                </div>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-6">
                        <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                            <div className="space-y-0.5">
                                <Label className="text-base font-semibold">Enable Problem Management</Label>
                                <p className="text-sm text-gray-500">Allow users to create and track problems in this project.</p>
                            </div>
                            <Switch 
                                checked={displayConfig.problemConfig?.enableProblemManagement}
                                onCheckedChange={(c) => isEditingGlobal && updateProblemConfig('enableProblemManagement', c)}
                                disabled={!isEditingGlobal}
                            />
                        </div>

                        {displayConfig.problemConfig?.enableProblemManagement && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label>RCA Requirement</Label>
                                    <Select 
                                        value={displayConfig.problemConfig?.rcaRequirement}
                                        onValueChange={(v) => updateProblemConfig('rcaRequirement', v)}
                                        disabled={!isEditingGlobal}
                                    >
                                        <SelectTrigger className="bg-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="always">Always Required</SelectItem>
                                            <SelectItem value="p1_p2_only">Required for P1/P2 Only</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-gray-500">Determines when Root Cause Analysis fields are mandatory.</p>
                                </div>
                            </div>
                        )}
                        
                        {!displayConfig.problemConfig?.enableProblemManagement && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center gap-3 text-gray-600">
                                <Info className="h-5 w-5" />
                                <span className="text-sm">Problem Management is explicitly marked <strong>Out of Scope</strong>.</span>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Workflow Configuration */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <GitBranch className="h-5 w-5 text-slate-500" />
                            Workflow Configuration
                        </CardTitle>
                        <CardDescription>
                            Define the lifecycle for this Work Type.
                        </CardDescription>
                    </div>
                    {!isEditingGlobal && !isProblem && (
                        <Button onClick={handleStartEditing} variant="outline" size="sm">
                            <Pencil className="h-3 w-3 mr-2" /> Edit Config
                        </Button>
                    )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                        <div className="p-4 border rounded-lg bg-slate-50">
                            <Label className="text-sm font-semibold text-slate-900 mb-2 block">Selected Workflow</Label>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-slate-800">{selectedWorkflow?.label}</div>
                                    <div className="text-xs text-slate-500">{selectedWorkflow?.description}</div>
                                </div>
                                <Badge variant="secondary" className="bg-blue-100 text-blue-700">Canonical</Badge>
                            </div>
                        </div>
                    </div>

                    {isChange && (
                        <div className="space-y-4 mt-4 border-t pt-4">
                            <Label className="text-sm font-semibold">Change Models</Label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {['Standard', 'Normal', 'Emergency'].map(type => (
                                    <div key={type} className="flex items-start space-x-2">
                                        <Checkbox 
                                            id={`change-${type}`} 
                                            checked={displayConfig.workflowConfig.changeTypes?.includes(type)}
                                            onCheckedChange={() => isEditingGlobal && toggleChangeType(type)}
                                            disabled={!isEditingGlobal}
                                        />
                                        <div className="grid gap-1.5 leading-none">
                                            <Label htmlFor={`change-${type}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                                {type} Change
                                            </Label>
                                            <p className="text-xs text-muted-foreground">
                                                {type === 'Standard' && 'Pre-authorized, low risk.'}
                                                {type === 'Normal' && 'Requires approval board (CAB).'}
                                                {type === 'Emergency' && 'Critical fix, expedited approval.'}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {isIncident && (
                        <div className="space-y-4 mt-4 border-t pt-4">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <div className="flex items-center gap-2">
                                        <Label className="text-base">Major Incident Process</Label>
                                        <GuidanceButton question={{
                                            id: 'major-incident-guidance',
                                            text: 'Major Incident Process',
                                            type: 'boolean',
                                            required: false,
                                            education: {
                                                topicId: 'major-incident',
                                                content: 'A specialized workflow for high-impact outages requiring coordinated response (War Room, Comms).',
                                                whyItMatters: 'Standard incident processes fail during a crisis. You need a dedicated protocol for communication and swarming.',
                                                examples: ['E-commerce site down', 'Email service outage', 'Security breach'],
                                                clarification: 'Major Incidents usually trigger PagerDuty/Opsgenie alerts.',
                                                reassurance: 'If you don\'t have a formal MI process yet, we can start with a simple "Critical" flag.'
                                            }
                                        } as any} />
                                    </div>
                                    <p className="text-sm text-muted-foreground">Enable specialized workflow for critical outages.</p>
                                </div>
                                <Switch 
                                    checked={displayConfig.workflowConfig.majorIncidentEnabled}
                                    onCheckedChange={(c) => isEditingGlobal && toggleMajorIncident(c)}
                                    disabled={!isEditingGlobal}
                                />
                            </div>
                        </div>
                    )}
                </div>
              </CardContent>
              {isEditingGlobal && !isProblem && (
                  <CardFooter className="bg-slate-50 border-t flex justify-end gap-2 py-3">
                      <Button variant="ghost" onClick={handleCancelEditing}>Cancel</Button>
                      <Button onClick={handleSaveGlobalConfig}>Save Changes</Button>
                  </CardFooter>
              )}
            </Card>

            {/* SLA & Priority Matrix (Hidden for PIR) */}
            {!isPIR && (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Clock className="h-5 w-5 text-slate-500" />
                                    SLA Targets (Hours)
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-6">
                                    <div>
                                        <h4 className="text-sm font-semibold mb-3 text-slate-700">Response Time</h4>
                                        <div className="grid grid-cols-4 gap-2">
                                            {['Critical', 'High', 'Medium', 'Low'].map(prio => (
                                                <div key={`resp-${prio}`} className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">{prio}</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={displayConfig.sla.response[prio]} 
                                                        onChange={(e) => updateSLA('response', prio, e.target.value)}
                                                        disabled={!isEditingGlobal}
                                                        className="h-8 text-sm"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold mb-3 text-slate-700">Resolution Time</h4>
                                        <div className="grid grid-cols-4 gap-2">
                                            {['Critical', 'High', 'Medium', 'Low'].map(prio => (
                                                <div key={`res-${prio}`} className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">{prio}</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={displayConfig.sla.resolution[prio]} 
                                                        onChange={(e) => updateSLA('resolution', prio, e.target.value)}
                                                        disabled={!isEditingGlobal}
                                                        className="h-8 text-sm"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <AlertCircle className="h-5 w-5 text-slate-500" />
                                    Priority Matrix
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs border-collapse">
                                        <thead>
                                            <tr>
                                                <th className="p-2 border bg-slate-100 text-slate-500">Impact \ Urgency</th>
                                                {displayConfig.priorityMatrix.urgency.map(u => (
                                                    <th key={u} className="p-2 border bg-slate-50 font-medium">{u}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {displayConfig.priorityMatrix.impact.map(i => (
                                                <tr key={i}>
                                                    <td className="p-2 border bg-slate-50 font-medium">{i}</td>
                                                    {displayConfig.priorityMatrix.urgency.map(u => (
                                                        <td key={`${i}-${u}`} className="p-1 border text-center">
                                                            <select 
                                                                className={`w-full h-full bg-transparent text-center focus:outline-none cursor-pointer font-semibold ${
                                                                    displayConfig.priorityMatrix.matrix[`${i}-${u}`] === 'Critical' ? 'text-red-600' :
                                                                    displayConfig.priorityMatrix.matrix[`${i}-${u}`] === 'High' ? 'text-orange-600' :
                                                                    displayConfig.priorityMatrix.matrix[`${i}-${u}`] === 'Medium' ? 'text-blue-600' : 'text-green-600'
                                                                }`}
                                                                value={displayConfig.priorityMatrix.matrix[`${i}-${u}`]}
                                                                onChange={(e) => isEditingGlobal && updateMatrixCell(i, u, e.target.value)}
                                                                disabled={!isEditingGlobal}
                                                            >
                                                                {['Critical', 'High', 'Medium', 'Low', 'Planning'].map(opt => (
                                                                    <option key={opt} value={opt}>{opt}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
          </TabsContent>

          {/* TAB B: Advanced Configuration */}
          <TabsContent value="advanced-config" className="space-y-6">
             <Card>
                 <CardHeader>
                     <CardTitle className="text-lg flex items-center gap-2">
                         <Zap className="h-5 w-5 text-amber-500" />
                         Advanced Configuration
                     </CardTitle>
                     <CardDescription>
                         Extended settings for specific modules.
                     </CardDescription>
                 </CardHeader>
                 <CardContent className="space-y-6">
                     
                     {/* Problem: Known Error Database */}
                     {isProblem && (
                         <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                             <div className="space-y-0.5">
                                 <Label className="text-base font-medium">Known Error Database</Label>
                                 <p className="text-sm text-gray-500">Maintain a separate repository of known errors and workarounds.</p>
                             </div>
                             <Switch 
                                checked={displayConfig.adv?.knownErrorDb}
                                onCheckedChange={(c) => isEditingGlobal && updateAdvanced('knownErrorDb', c)}
                                disabled={!isEditingGlobal}
                             />
                         </div>
                     )}

                     {/* Incident: PIR Trigger */}
                     {isIncident && (
                         <div className="space-y-4">
                             <div className="space-y-2">
                                 <div className="flex items-center gap-2">
                                     <Label>PIR Trigger Policy</Label>
                                     <GuidanceButton question={{
                                            id: 'pir-trigger-guidance',
                                            text: 'PIR Trigger Policy',
                                            type: 'text',
                                            required: false,
                                            education: {
                                                topicId: 'pir-trigger',
                                                content: 'Defines which incidents automatically generate a Post-Incident Review ticket.',
                                                whyItMatters: 'Consistent reviews prevent recurring outages. Automating the trigger ensures you never miss a learning opportunity.',
                                                examples: ['Major Incidents Only (Recommended)', 'All P1s', 'Manual (Ad-hoc)'],
                                                clarification: 'The PIR is a separate ticket linked to the original incident.',
                                                reassurance: 'You can always create a PIR manually even if the trigger criteria aren\'t met.'
                                            }
                                        } as any} />
                                 </div>
                                 <Select 
                                     value={displayConfig.adv?.pirTrigger || 'major_only'}
                                     onValueChange={(v) => isEditingGlobal && updateAdvanced('pirTrigger', v)}
                                     disabled={!isEditingGlobal}
                                 >
                                     <SelectTrigger>
                                         <SelectValue />
                                     </SelectTrigger>
                                     <SelectContent>
                                         <SelectItem value="major_only">Major Incidents Only</SelectItem>
                                         <SelectItem value="all_p1">All P1 Incidents</SelectItem>
                                         <SelectItem value="manual">Manual Selection</SelectItem>
                                     </SelectContent>
                                 </Select>
                             </div>
                         </div>
                     )}

                     {/* Change: Standard Change Catalog */}
                     {isChange && (
                         <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                             <div className="space-y-0.5">
                                 <Label className="text-base font-medium">Standard Change Catalog</Label>
                                 <p className="text-sm text-gray-500">Enable pre-approved change templates.</p>
                             </div>
                             <Switch 
                                checked={displayConfig.adv?.stdCatalog}
                                onCheckedChange={(c) => isEditingGlobal && updateAdvanced('stdCatalog', c)}
                                disabled={!isEditingGlobal}
                             />
                         </div>
                     )}

                     {isEditingGlobal && (
                        <div className="flex justify-end pt-4">
                            <Button onClick={handleSaveGlobalConfig}>Save Advanced Config</Button>
                        </div>
                     )}
                 </CardContent>
             </Card>
          </TabsContent>

          {/* TAB C: Request Types (Layer 2) */}
          <TabsContent value="request-types" className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Request Types</h3>
              <Button onClick={() => handleOpenWizard()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Request Type
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Existing Request Types would go here */}
              <div className="col-span-full text-center py-12 bg-white rounded-lg border border-dashed border-gray-300 text-gray-500">
                  <List className="h-10 w-10 mx-auto mb-3 opacity-20" />
                  <p>No request types defined yet.</p>
                  <Button variant="link" onClick={() => handleOpenWizard()}>Create your first one</Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* WIZARD DIALOG */}
      <Dialog open={isWizardOpen} onOpenChange={handleCloseWizard}>
        <DialogContent className="max-w-6xl h-[90vh] p-0 overflow-hidden flex flex-col">
            <RequestTypeFormBuilder 
                serviceProjectId={workType.id}
                requestType={selectedRequestTypeId ? requestTypes.find(rt => rt.id === selectedRequestTypeId) : null}
                companyId={workType.companyId}
                sowId={sowId}
                accessToken={accessToken}
                userRole="consultant" // Consultant can edit locked logic
                onSave={async (data) => {
                    // Save logic
                    handleCloseWizard();
                    toast.success("Request Type Saved");
                }}
                onCancel={handleCloseWizard}
                inheritedConfig={{
                    workflowConfig: displayConfig.workflowConfig,
                    sla: displayConfig.sla,
                    priorityMatrix: displayConfig.priorityMatrix,
                    riskModel: displayConfig.riskModel,
                    approvals: displayConfig.approvals,
                    blackouts: displayConfig.blackouts
                }}
            />
        </DialogContent>
      </Dialog>

      {/* SPACE ARCHITECTURE WIZARD */}
      <Dialog open={isSpaceWizardOpen} onOpenChange={(open) => {
          if (!open && (!spaceArchitecture?.locked)) {
              // Warn if closing without locking?
          }
          setIsSpaceWizardOpen(open);
      }}>
          <DialogContent className="max-w-5xl h-[85vh] p-0 overflow-hidden">
              <SpaceArchitectureWizard 
                  initialData={spaceArchitecture || undefined}
                  onSave={handleSpaceArchitectureSave}
                  onCancel={() => setIsSpaceWizardOpen(false)}
              />
          </DialogContent>
      </Dialog>

    </div>
  );
}
